import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { adminAmyEvents, type AmyEventRow } from "@/lib/admin-amy.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/amy")({
  head: () => ({
    meta: [
      { title: "Amy events — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AmyEventsPage,
});

const EVENT_TYPES = [
  "first_message",
  "lead_submitted",
  "follow_up_consent",
  "follow_up_sent",
] as const;

const EVENT_LABEL: Record<string, string> = {
  first_message: "Chat started",
  lead_submitted: "Lead submitted",
  follow_up_consent: "Follow-up consent",
  follow_up_sent: "Follow-up email sent",
};

const EVENT_TONE: Record<string, string> = {
  first_message: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  lead_submitted: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  follow_up_consent: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  follow_up_sent: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
};

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function csvEscape(v: string | null | undefined): string {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCSV(filename: string, rows: AmyEventRow[]) {
  const header = [
    "id",
    "event_type",
    "session_id",
    "application_id",
    "created_at",
    "metadata_json",
  ];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.id,
        r.event_type,
        r.session_id,
        r.application_id,
        r.created_at,
        r.metadata_json,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AmyEventsPage() {
  const fetchEvents = useServerFn(adminAmyEvents);

  const defaultUntil = useMemo(() => toISODate(new Date()), []);
  const defaultSince = useMemo(
    () => toISODate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
    [],
  );

  const [since, setSince] = useState(defaultSince);
  const [until, setUntil] = useState(defaultUntil);
  const [types, setTypes] = useState<Record<string, boolean>>({
    first_message: true,
    lead_submitted: true,
    follow_up_consent: true,
    follow_up_sent: true,
  });

  const activeTypes = EVENT_TYPES.filter((t) => types[t]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["admin", "amy-events", since, until, activeTypes.join(",")],
    queryFn: () =>
      fetchEvents({
        data: {
          since: new Date(`${since}T00:00:00.000Z`).toISOString(),
          until: new Date(`${until}T23:59:59.999Z`).toISOString(),
          types: activeTypes,
        },
      }),
    staleTime: 30_000,
  });

  const canExport = !!data && data.recent.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Amy — AI Assistant</h1>
        <p className="text-sm text-muted-foreground">
          Key chat events. Filter by date range and event type, then export as
          CSV.
        </p>
      </header>

      {/* Filters */}
      <section
        className="mb-6 rounded-md border p-4 grid gap-4 md:grid-cols-[auto_auto_1fr_auto] md:items-end"
        aria-label="Event filters"
      >
        <div className="grid gap-1.5">
          <Label htmlFor="since">From</Label>
          <Input
            id="since"
            type="date"
            value={since}
            max={until}
            onChange={(e) => setSince(e.target.value)}
            className="w-full md:w-auto"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="until">To</Label>
          <Input
            id="until"
            type="date"
            value={until}
            min={since}
            max={defaultUntil}
            onChange={(e) => setUntil(e.target.value)}
            className="w-full md:w-auto"
          />
        </div>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">Event types</legend>
          <div className="flex flex-wrap gap-3">
            {EVENT_TYPES.map((t) => (
              <label
                key={t}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={types[t]}
                  onCheckedChange={(v) =>
                    setTypes((prev) => ({ ...prev, [t]: v === true }))
                  }
                  aria-label={EVENT_LABEL[t]}
                />
                {EVENT_LABEL[t]}
              </label>
            ))}
          </div>
        </fieldset>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            data && downloadCSV(`amy-events-${since}_to_${until}.csv`, data.recent)
          }
          disabled={!canExport}
          aria-label="Export filtered events as CSV"
          className="justify-self-start md:justify-self-end"
        >
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Export CSV
        </Button>
      </section>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      {error && (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      )}

      {data && (
        <>
          <div
            className="grid gap-4 grid-cols-2 md:grid-cols-5 mb-8"
            aria-busy={isFetching}
          >
            <StatCard
              label="Chats started"
              value={data.totals.first_message}
              hint="unique first messages"
            />
            <StatCard
              label="Leads submitted"
              value={data.totals.lead_submitted}
            />
            <StatCard
              label="Follow-up consent"
              value={data.totals.follow_up_consent}
              hint="opted into 48h email"
            />
            <StatCard
              label="Follow-up sent"
              value={data.totals.follow_up_sent}
            />
            <StatCard
              label="Chat → lead"
              value={`${data.totals.conversionRate.toFixed(1)}%`}
            />
          </div>

          <section className="rounded-md border p-4">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="text-sm font-semibold">
                Recent events{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  ({data.recent.length})
                </span>
              </h2>
            </div>
            {data.recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No events match the current filters.
              </p>
            ) : (
              <ul className="divide-y">
                {data.recent.map((r) => (
                  <li
                    key={r.id}
                    className="py-2 flex items-start justify-between gap-3 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          className={`${EVENT_TONE[r.event_type] ?? "bg-muted text-foreground"} border-0`}
                          variant="outline"
                        >
                          {EVENT_LABEL[r.event_type] ?? r.event_type}
                        </Badge>
                        {r.session_id && (
                          <span className="text-[10px] text-muted-foreground font-mono">
                            session {r.session_id.slice(0, 8)}
                          </span>
                        )}
                        {r.application_id && (
                          <span className="text-[10px] text-muted-foreground font-mono">
                            app {r.application_id.slice(0, 8)}
                          </span>
                        )}
                      </div>
                      {r.metadata_json && r.metadata_json !== "{}" && (
                        <pre className="mt-1 text-[11px] text-muted-foreground whitespace-pre-wrap break-words">
                          {r.metadata_json}
                        </pre>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground shrink-0">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {hint && (
        <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
      )}
    </div>
  );
}
